import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface MatchingEngineListing {
  id: string;
  title: string;
  price_usd: number | null;
  manufacturer: string;
  model: string;
  variant: string | null;
  total_tsn: number | null;
  total_csn: number | null;
  overall_condition: string | null;
  city: string | null;
  country: string | null;
  photos: string[] | null;
}

export const useMatchingEngineListings = (
  requestManufacturer: string | null,
  requestModel: string | null,
  requestListingType: string,
  userId: string | null
) => {
  const [matchingListings, setMatchingListings] = useState<MatchingEngineListing[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (userId) {
      fetchMatchingListings();
    }
  }, [requestManufacturer, requestModel, requestListingType, userId]);

  const fetchMatchingListings = async () => {
    if (!userId) return;

    try {
      setLoading(true);

      // Determine the matching listing type based on request type
      const matchingType = (() => {
        if (requestListingType === 'wanted') return 'sale';
        if (requestListingType === 'buy_sale') return 'sale';
        if (requestListingType === 'buy_lease') return 'lease';
        if (requestListingType === 'buy_rent') return 'rent';
        return 'sale';
      })();

      let query = supabase
        .from('engine_listings')
        .select('*')
        .eq('user_id', userId)
        .eq('listing_type', matchingType)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(10);

      // Add manufacturer filter if specified
      if (requestManufacturer) {
        query = query.ilike('manufacturer', `%${requestManufacturer}%`);
      }

      // Add model filter if specified
      if (requestModel) {
        query = query.ilike('model', `%${requestModel}%`);
      }

      const { data, error } = await query;

      if (error) throw error;

      setMatchingListings(data || []);
    } catch (error) {
      console.error('Error fetching matching engine listings:', error);
      toast({
        title: "Error loading listings",
        description: "Failed to load your matching engine listings.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return { matchingListings, loading, refetch: fetchMatchingListings };
};
